import { scenes } from './editorModel';
export const personalScenes = scenes.filter((s) => s !== 'duet');
export const archiveScope = (origin, id) => JSON.stringify([origin, String(id)]);
const DB = 'postpop-edition-archives-v1',
  STORE = 'archives';
export async function validateArchive(row) {
  if (
    !row ||
    typeof row.id !== 'string' ||
    typeof row.scope !== 'string' ||
    !Number.isFinite(Date.parse(row.createdAt)) ||
    row.version !== 1 ||
    JSON.stringify(row.scenes) !== JSON.stringify(personalScenes) ||
    !Array.isArray(row.images) ||
    row.images.length !== 12
  )
    throw Error('Invalid archive');
  const payload = JSON.stringify(row.payload);
  if (
    new TextEncoder().encode(payload).length > 8000000 ||
    row.payload?.version !== 1 ||
    !Array.isArray(row.payload.records)
  )
    throw Error('Invalid records');
  if (
    'comparison' in row.payload ||
    'friendInput' in (row.payload.settings ?? {}) ||
    'friendNickname' in (row.payload.settings ?? {})
  )
    throw Error('Friend data must not be retained');
  let total = 0;
  for (const blob of row.images) {
    if (!(blob instanceof Blob) || blob.type !== 'image/png' || blob.size > 18000000)
      throw Error('Invalid image');
    total += blob.size;
    const b = new Uint8Array(await blob.slice(0, 24).arrayBuffer());
    if (b.length !== 24 || ![137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => b[i] === v))
      throw Error('Invalid PNG');
    const v = new DataView(b.buffer),
      w = v.getUint32(16),
      h = v.getUint32(20);
    if (!w || !h || w > 3000 || h > 4000) throw Error('Invalid dimensions');
  }
  if (total > 60000000) throw Error('Archive too large');
}
function open() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(Error('Storage unavailable'));
      return;
    }
    let blocked = false;
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      const s = r.result.createObjectStore(STORE, { keyPath: ['scope', 'id'] });
      s.createIndex('scope', 'scope');
    };
    r.onerror = () => reject(r.error);
    r.onblocked = () => {
      blocked = true;
      reject(Error('Storage blocked'));
    };
    r.onsuccess = () => {
      const db = r.result;
      if (blocked) {
        db.close();
        return;
      }
      db.onversionchange = () => db.close();
      resolve(db);
    };
  });
}
async function transaction(mode, signal, work) {
  if (signal?.aborted) throw signal.reason ?? new Error('Cancelled');
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode),
        s = tx.objectStore(STORE);
      let result, failure;
      const abort = () => {
        try {
          tx.abort();
        } catch {}
      };
      signal?.addEventListener('abort', abort, { once: true });
      const done = () => signal?.removeEventListener('abort', abort);
      tx.oncomplete = () => {
        done();
        resolve(result);
      };
      tx.onabort = () => {
        done();
        reject(signal?.aborted ? signal.reason : (failure ?? tx.error ?? Error('Storage failed')));
      };
      tx.onerror = () => {
        failure = tx.error;
      };
      if (signal?.aborted) {
        abort();
        return;
      }
      try {
        work(
          s,
          tx,
          (value) => {
            result = value;
          },
          (error) => {
            failure = error;
            abort();
          },
        );
      } catch (e) {
        failure = e;
        abort();
      }
    });
  } finally {
    db.close();
  }
}
export function listArchives(scope, signal) {
  return transaction('readonly', signal, (s, tx, done, fail) => {
    const q = s.index('scope').getAll(scope);
    q.onerror = () => fail(q.error);
    q.onsuccess = () => {
      const rows = q.result;
      if (
        rows.some(
          (r) =>
            r.version !== 1 ||
            !r.id ||
            r.scope !== scope ||
            !Number.isFinite(Date.parse(r.createdAt)) ||
            r.images?.some((image) => !(image instanceof Blob) || image.type !== 'image/png') ||
            !Array.isArray(r.images) ||
            r.images.length !== 12 ||
            JSON.stringify(r.scenes) !== JSON.stringify(personalScenes),
        )
      )
        return fail(Error('Corrupt archive'));
      done(rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    };
  });
}
export async function saveArchive(row, signal, isCurrent = () => true) {
  await validateArchive(row);
  if (!isCurrent()) throw Error('Snapshot changed');
  return transaction('readwrite', signal, (s, tx, done, fail) => {
    const q = s.index('scope').count(row.scope);
    q.onsuccess = () => {
      if (!isCurrent()) return fail(Error('Snapshot changed'));
      if (q.result >= 5) return fail(Error('Archive full'));
      const add = s.add(row);
      add.onsuccess = () => done(row);
    };
  });
}
export function removeArchive(scope, id, signal) {
  return transaction('readwrite', signal, (s, tx, done) => {
    s.delete([scope, id]);
    done(id);
  });
}
export function archivePayload(artwork, asOf, locale) {
  const { edition } = artwork;
  return {
    version: 1,
    asOf: asOf.toISOString(),
    period: artwork.period,
    locale,
    records: edition.records,
    settings: Object.fromEntries(
      [
        'theme',
        'format',
        'template',
        'intro',
        'cardHeading',
        'selectedIds',
        'quoteId',
        'quoteStart',
        'quoteLength',
        'showName',
        'showStats',
        'showCovers',
        'artistIntroId',
      ].map((k) => [k, artwork[k]]),
    ),
    persona: artwork.persona ?? null,
  };
}
export async function captureArchive({
  scope,
  artwork,
  asOf,
  locale,
  signal,
  isCurrent,
  exporter,
  onProgress,
  personaReason,
}) {
  const payload = archivePayload(artwork, asOf, locale),
    images = [];
  const check = () => {
    if (signal.aborted) throw signal.reason ?? new Error('Cancelled');
    if (!isCurrent()) throw Error('Snapshot changed');
  };
  for (const [index, scene] of personalScenes.entries()) {
    check();
    onProgress(index + 1, 12);
    const result = await exporter(
      {
        ...artwork,
        scene,
        comparison: undefined,
        emptyReason: scene === 'persona' ? personaReason : undefined,
        motionProgress: 1,
        animateCharts: false,
      },
      { signal },
    );
    check();
    images.push(result.blob);
  }
  check();
  return {
    version: 1,
    id: crypto.randomUUID(),
    scope,
    createdAt: new Date().toISOString(),
    scenes: personalScenes,
    images,
    payload,
  };
}
